import clsx from "clsx";
import React from "react";
import environment from "web/script/modules/environment";
import * as styles from "./product-gallery-image.css";

export interface Image {
    id: string | number;
    altText?: string | null;
    url: string;
    src?: string;
}

export interface ProductGalleryImageProps {
    src: string;
    alt?: string;
    onClick?: (event: React.MouseEvent<HTMLImageElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLImageElement>) => void;
    width?: number;
    height?: number;
    isFullscreen?: boolean;
    lazy?: boolean;
}

export function buildUrl(url: string, width: number, height: number): string {
    return url.replace("/photos/", `/${width}/${height}/n/photos/`);
}

export function ProductGalleryImage({
    src,
    alt,
    lazy = false,
    onClick,
    onKeyDown,
    width = 520,
    height = 650,
    isFullscreen = false,
}: ProductGalleryImageProps): React.ReactElement {
    // CW-2172 increase dimension of images in IS PDP
    const showHigherResolutionImages = environment.getFeature(
        "seo_higher_resolution_pdp_images_uk"
    );
    const showHighQualityFullscreenImages = !!(
        isFullscreen && environment.getFeature("beam_web_pdp_gallery_fullscreen_quality_increase")
    );
    const mobileRatio = { width: 520, height: 650 };
    const desktopRatio = { width: 1040, height: 1300 };
    const fullscreenRatio = { width: 2400, height: 3000 };
    const responsiveMobileRatio = showHighQualityFullscreenImages ? fullscreenRatio : mobileRatio;
    const responsiveDesktopRatio = showHighQualityFullscreenImages ? fullscreenRatio : desktopRatio;

    const wrapperClassName = clsx({
        [styles.item]: !isFullscreen,
        [styles.fullScreenItem]: isFullscreen,
    });

    const imgClassName = clsx({
        [styles.image]: !isFullscreen,
        [styles.fullscreenImage]: isFullscreen,
    });

    return (
        <div
            className={wrapperClassName}
            onClick={onClick}
            onKeyDown={onKeyDown}
            role="button"
            tabIndex={0}
            data-testid="product-gallery-image"
        >
            {showHigherResolutionImages || showHighQualityFullscreenImages ? (
                <picture
                    className={clsx({
                        [styles.image]: !isFullscreen,
                        [styles.fullscreenImage]: isFullscreen,
                    })}
                    data-testid="product-gallery-image-picture"
                >
                    <source
                        media={styles.mobileMedia}
                        srcSet={buildUrl(
                            src,
                            responsiveMobileRatio.width,
                            responsiveMobileRatio.height
                        )}
                    />
                    <source
                        media={styles.desktopMedia}
                        srcSet={buildUrl(
                            src,
                            responsiveDesktopRatio.width,
                            responsiveDesktopRatio.height
                        )}
                    />
                    <img
                        decoding="async"
                        alt={alt}
                        loading={lazy ? "lazy" : "eager"}
                        width={width}
                        src={buildUrl(
                            src,
                            responsiveDesktopRatio.width,
                            responsiveDesktopRatio.height
                        )}
                        data-testid="product-gallery-image-img"
                    />
                </picture>
            ) : (
                <img
                    className={imgClassName}
                    decoding="async"
                    loading={lazy ? "lazy" : "eager"}
                    src={buildUrl(src, width, height)}
                    alt={alt}
                    width={width}
                    data-testid="product-gallery-image-img"
                />
            )}
        </div>
    );
}
